<template>
  <div v-if="allowedActions.length">
    <v-menu v-model="isMenuOpened">
      <template v-slot:activator="{ props }">
        <MenuActivator v-bind="props" data-cy="devicePopoverDropdown" />
      </template>
      <v-list v-if="isMenuOpened" data-cy="devicePopoverMenuOptions">
        <!-- Device Info -->
        <v-list-item
          v-if="canPerformAction(actionsEnum.DEVICE_INFO)"
          :title="$t('map.popover.menu.deviceInfo')"
          prepend-icon="mdi-information"
          @click="openInfo"
          data-cy="deviceInfoActivator"
        />
        <!-- Device Settings -->
        <v-list-item
          v-if="canPerformAction(actionsEnum.SETTINGS)"
          :title="$t('map.popover.menu.deviceSettings')"
          prepend-icon="mdi-cog-box"
          @click="openSettings"
          data-cy="deviceSettingsActivator"
        />
        <!-- Device Alerts -->
        <v-list-item
          v-if="canPerformAction(actionsEnum.ALERT_SETUP)"
          :title="$t('map.popover.menu.deviceAlerts')"
          prepend-icon="mdi-bell"
          @click="openAlerts"
          data-cy="deviceAlertsActivator"
        />
        <!-- Device Sharing -->
        <v-list-item
          v-if="canPerformAction(actionsEnum.SHARING)"
          :title="$t('map.popover.menu.deviceSharing')"
          prepend-icon="mdi-share-variant"
          @click="openSharing"
          data-cy="deviceSharing"
        />
        <!-- History -->
        <v-list-item
          v-if="canPerformAction(actionsEnum.HISTORY)"
          :title="$t('map.popover.menu.deviceHistory')"
          prepend-icon="mdi-chart-line"
          @click="openHistory"
          data-cy="historyActivator"
        />
        <!-- Current Readings -->
        <v-list-item
          v-if="canPerformAction(actionsEnum.CURRENT_READINGS)"
          :title="$t('manageDevices.actions.currentReadings')"
          prepend-icon="mdi-newspaper-variant-outline"
          @click="openCurrentReadings"
          data-cy="currentReadingsActivator"
        />
        <!-- View on Map -->
        <ViewOnMapMenu
          v-if="canPerformAction(actionsEnum.VIEW_ON_MAP)"
          :deviceId="deviceId"
          :permissions="permissions"
          :accessLevel="accessLevel"
        />
      </v-list>
    </v-menu>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { canAccess, Access } from '@/services/device-access'
import { getPermissionsByDeviceId } from '@/services/subscriptions'
import { SlugsEnum } from '@/permissions/SlugsEnum'
import MenuActivator from '@/components/common/buttons/MenuActivator.vue'
import ViewOnMapMenu from './viewOnMapMenu.vue'

const ACTIONS_ENUM = {
  DEVICE_INFO: 'DeviceInfo',
  SETTINGS: 'MenuSettings',
  ALERT_SETUP: 'MenuAlertSetup',
  SHARING: 'MenuSharing',
  HISTORY: 'MenuHistory',
  VIEW_ON_MAP: 'MenuViewOnMap',
  CURRENT_READINGS: 'MenuCurrentReadings'
} as const

export default defineComponent({
  name: 'DeviceActionMenu',
  props: {
    deviceId: {
      type: String,
      default: ''
    },
    accessLevel: {
      type: Number as PropType<Access>,
      default: 0
    }
  },
  data() {
    return {
      isMenuOpened: false,
      actionsEnum: ACTIONS_ENUM
    }
  },
  components: { ViewOnMapMenu, MenuActivator },
  methods: {
    canPerformAction(slugName: string): boolean {
      switch (slugName) {
        case this.actionsEnum.DEVICE_INFO:
          return canAccess(Access.FULL, this.accessLevel)
        case this.actionsEnum.SETTINGS:
          return canAccess(Access.FULL, this.accessLevel)
        case this.actionsEnum.ALERT_SETUP:
          return (
            canAccess(Access.FULL, this.accessLevel) &&
            this.hasDevicePermission(SlugsEnum.MenuAlertSetup)
          )
        case this.actionsEnum.SHARING:
          return (
            canAccess(Access.FULL, this.accessLevel) &&
            this.hasDevicePermission(SlugsEnum.MenuSharing)
          )
        case this.actionsEnum.HISTORY:
          return (
            canAccess(Access.SHARED, this.accessLevel) &&
            this.hasUserPermission(SlugsEnum.MenuHistory) &&
            this.hasDevicePermission(SlugsEnum.MenuHistory)
          )
        case this.actionsEnum.VIEW_ON_MAP:
          return (
            canAccess(Access.SHARED, this.accessLevel) &&
            this.hasUserPermission(SlugsEnum.MapViewPin) &&
            this.hasDevicePermission(SlugsEnum.MapViewPin)
          )
        case this.actionsEnum.CURRENT_READINGS:
          return (
            canAccess(Access.SHARED, this.accessLevel) &&
            this.hasUserPermission(SlugsEnum.MenuCurrentReadings) &&
            this.hasDevicePermission(SlugsEnum.MenuCurrentReadings)
          )
      }
      return false
    },
    hasUserPermission(slugName: string): boolean {
      return this.$permissions.isAllowed(slugName)
    },
    hasDevicePermission(slugName: string): boolean {
      return this.permissions.includes(slugName)
    },
    openInfo() {
      this.openDialog('info')
    },
    openSettings() {
      this.openDialog('settings')
    },
    openAlerts() {
      this.openDialog('alerts')
    },
    openCurrentReadings() {
      this.openDialog('currentReadings')
    },
    openSharing() {
      this.openDialog('sharing')
    },
    openHistory() {
      this.openDialog('history')
    },
    openDialog(dialog: string): void {
      this.$emit('openDialog', {
        dialog,
        deviceId: this.deviceId
      })
      this.isMenuOpened = false
    }
  },
  computed: {
    permissions(): string[] {
      return getPermissionsByDeviceId(this.deviceId) || []
    },
    allowedActions(): string[] {
      const slugNames = Object.values(this.actionsEnum)
      return slugNames.filter((n) => this.canPerformAction(n))
    }
  }
})
</script>
